import React, { useMemo, useState } from "react"
import moment from "moment"
import { MdAccountBalance, MdCheckCircleOutline, MdDescription, MdOutlineAttachMoney, MdOutlinePayments } from "react-icons/md"
import Modal from "../index"
import Input from "../../Form/Input"
import FormGroup from "../../Form/FormGroup"
import Button from "../../Form/Button"
import Select from "../../Form/Select"
import DatePicker from "../../Form/DatePicker"
import Textarea from "../../Form/Textarea"
import FormGrid from "../../Form/FormGrid"
import FormSection from "../../Form/FormSection"
import OptionCardSelector from "../../Form/OptionCardSelector"
import { formatCurrency } from "../../../utils/formatter"
import styles from "./modalTransactions.module.css"

const operationOptions = [
    { id: "CHARGE", value: "Emitir cobrança", description: "Gera boleto com PIX ou link para cartão." },
    { id: "REGISTER_PAYMENT", value: "Registrar recebimento", description: "Registra um valor já pago fora da Oktto." },
    { id: "INVOICE_ONLY", value: "Emitir somente NF", description: "Registra o pagamento e emite a nota sem gerar cobrança." },
]

const chargeMethods = [
    { id: "BANK_SLIP", value: "Boleto com PIX" },
    { id: "CREDIT_CARD", value: "Cartão de crédito" },
]

const receivedMethods = [
    { id: "PIX", value: "PIX" },
    { id: "BANK_TRANSFER", value: "Transferência bancária" },
    { id: "CREDIT_CARD", value: "Cartão de crédito externo" },
    { id: "DEBIT_CARD", value: "Cartão de débito" },
    { id: "BANK_SLIP", value: "Boleto já pago" },
    { id: "CASH", value: "Dinheiro" },
    { id: "OTHER", value: "Outro meio" },
]

const chargeInvoiceOptions = [
    { id: "NO", value: "Emitir no recebimento", description: "Padrão: a NF será emitida automaticamente quando a cobrança for paga." },
    { id: "YES", value: "Emitir agora", description: "Emite a NF junto com a criação da cobrança." },
]

const invoiceOptions = [
    { id: "NO", value: "Não emitir agora", description: "Use se a NF deste valor já foi emitida. Não haverá emissão automática." },
    { id: "YES", value: "Emitir Nota Fiscal", description: "A emissão fiscal será solicitada junto com o lançamento." },
]

const ModalNewTransaction = ({ listTypes = [], onSubmit = () => { }, closeModal = () => { } }) => {
    const today = moment().format("YYYY-MM-DD")
    const [transaction, setTransaction] = useState({
        operation_mode: "CHARGE",
        type: "CUSTOM_SERVICE",
        method: "BANK_SLIP",
        discount: "0",
        emit_date: today,
        due_date: moment().add(7, "days").format("YYYY-MM-DD"),
        paid_at: today,
        issue_invoice: false,
    })

    const handleChangeValue = (name, value) => setTransaction(previous => ({ ...previous, [name]: value }))
    const setOperationMode = operationMode => setTransaction(previous => ({
        ...previous,
        operation_mode: operationMode,
        method: operationMode === "CHARGE" ? "BANK_SLIP" : "PIX",
        issue_invoice: operationMode === "INVOICE_ONLY",
    }))

    const netValue = useMemo(() => Math.max(0, (Number(transaction.value) || 0) - (Number(transaction.discount) || 0)), [transaction.value, transaction.discount])
    const isCharge = transaction.operation_mode === "CHARGE"
    const willIssueInvoice = isCharge || transaction.operation_mode === "INVOICE_ONLY" || transaction.issue_invoice
    const requiresFiscalDescription = willIssueInvoice && transaction.type === "CUSTOM_SERVICE"
    const invalid = !transaction.type || !(Number(transaction.value) > 0) || Number(transaction.discount || 0) >= Number(transaction.value || 0) || !transaction.method ||
        (isCharge ? (!transaction.emit_date || !transaction.due_date) : !transaction.paid_at) ||
        (requiresFiscalDescription && !transaction.invoice_description?.trim())

    const handleSave = event => {
        event.preventDefault()
        if (!invalid) onSubmit(transaction)
    }

    return (
        <Modal icon={<MdOutlineAttachMoney />} title="Novo lançamento financeiro" description="Escolha primeiro o resultado desejado; cobrança, recebimento e Nota Fiscal são operações independentes." closeModal={closeModal} fullWidith={true}>
            <form className={styles.entryForm} onSubmit={handleSave}>
                <FormSection title="O que deseja fazer?" description="Essa escolha define se algum meio de cobrança será realmente criado.">
                    <OptionCardSelector single options={operationOptions} selected={[transaction.operation_mode]} onChange={values => setOperationMode(values[0])} renderIcon={option => option.id === "CHARGE" ? <MdOutlinePayments /> : option.id === "REGISTER_PAYMENT" ? <MdCheckCircleOutline /> : <MdDescription />} />
                </FormSection>

                <div className={styles.entryLayout}>
                    <div className={styles.entryFields}>
                        <FormSection title="Dados do lançamento" description="Classificação e valores que aparecerão no histórico financeiro.">
                            <FormGrid>
                                <FormGroup labelText="Tipo">
                                    <Select name="Tipo" placeholder="Selecione o tipo" options={listTypes} selected={transaction.type} onChange={value => handleChangeValue("type", value)} multiple={false} />
                                </FormGroup>
                                <FormGroup labelText="Referência externa">
                                    <Input type="text" maxLength={120} placeholder="Ex.: ID do PIX ou pedido interno" value={transaction.external_reference || ""} onChange={event => handleChangeValue("external_reference", event.target.value)} />
                                </FormGroup>
                                <FormGroup labelText="Valor bruto">
                                    <Input type="currency" placeholder="Valor" value={transaction.value || ""} onChange={event => handleChangeValue("value", event.target.value)} />
                                </FormGroup>
                                <FormGroup labelText="Desconto">
                                    <Input type="currency" placeholder="Desconto" value={transaction.discount || ""} onChange={event => handleChangeValue("discount", event.target.value)} />
                                </FormGroup>
                                <FormGroup labelText={isCharge ? "Meio de cobrança" : "Forma como foi recebido"}>
                                    <Select name="Forma de pagamento" placeholder="Selecione" options={isCharge ? chargeMethods : receivedMethods} selected={transaction.method} onChange={value => handleChangeValue("method", value)} multiple={false} />
                                </FormGroup>
                                {isCharge ? <>
                                    <FormGroup labelText="Data de emissão">
                                        <DatePicker name="DD/MM/YYYY" multiple={false} dateStart={transaction.emit_date} onChange={value => handleChangeValue("emit_date", value)} min={today} />
                                    </FormGroup>
                                    <FormGroup labelText="Data de vencimento">
                                        <DatePicker name="DD/MM/YYYY" multiple={false} dateStart={transaction.due_date} onChange={value => handleChangeValue("due_date", value)} min={transaction.emit_date || today} />
                                    </FormGroup>
                                </> :
                                    <FormGroup labelText="Data do recebimento">
                                        <DatePicker name="DD/MM/YYYY" multiple={false} dateStart={transaction.paid_at} onChange={value => handleChangeValue("paid_at", value)} max={today} />
                                    </FormGroup>
                                }
                            </FormGrid>
                            <FormGroup labelText="Observação interna">
                                <Textarea rows={3} maxLength={500} placeholder="Contexto da negociação ou informação para o financeiro." value={transaction.observation || ""} onChange={event => handleChangeValue("observation", event.target.value)} />
                            </FormGroup>
                        </FormSection>

                        <FormSection title="Documento fiscal" description={transaction.operation_mode === "INVOICE_ONLY" ? "A Nota Fiscal será emitida sem gerar boleto ou link de pagamento." : "Para cobranças, escolha emitir a NF no recebimento ou agora. Para valores já recebidos, informe se é necessário emitir."}>
                            {transaction.operation_mode !== "INVOICE_ONLY" &&
                                <OptionCardSelector single options={isCharge ? chargeInvoiceOptions : invoiceOptions} selected={[transaction.issue_invoice ? "YES" : "NO"]} onChange={values => handleChangeValue("issue_invoice", values[0] === "YES")} renderIcon={option => option.id === "YES" ? <MdDescription /> : <MdAccountBalance />} />
                            }
                            {willIssueInvoice &&
                                <FormGroup labelText={requiresFiscalDescription ? "Descrição da Nota Fiscal *" : "Descrição da Nota Fiscal"}>
                                    <Textarea rows={3} maxLength={300} placeholder="Descrição do serviço que constará na NFS-e. Se ficar vazio, será usada a descrição padrão do tipo." value={transaction.invoice_description || ""} onChange={event => handleChangeValue("invoice_description", event.target.value)} />
                                </FormGroup>
                            }
                        </FormSection>
                    </div>

                    <aside className={styles.entrySummary}>
                        <small>Resumo do lançamento</small>
                        <strong>{formatCurrency(netValue)}</strong>
                        <dl>
                            <div><dt>Operação</dt><dd>{operationOptions.find(option => option.id === transaction.operation_mode)?.value}</dd></div>
                            <div><dt>Situação inicial</dt><dd>{isCharge ? "Aguardando pagamento" : "Pago"}</dd></div>
                            <div><dt>Cobrança externa</dt><dd>{isCharge ? "Será gerada" : "Não será gerada"}</dd></div>
                            <div><dt>Nota Fiscal</dt><dd>{transaction.issue_invoice ? "Emitir agora" : isCharge ? "Emitir no recebimento" : "Sem emissão automática"}</dd></div>
                        </dl>
                        {!isCharge && <p>Nenhum boleto ou link será criado. O pagamento ficará registrado como recebido por {receivedMethods.find(method => method.id === transaction.method)?.value || "meio externo"}.</p>}
                    </aside>
                </div>

                <footer className={styles.entryActions}>
                    <Button variant="secondary" type="button" fullWidth={false} onClick={closeModal}>Cancelar</Button>
                    <Button variant="primary" type="submit" fullWidth={false} disabled={invalid}>{transaction.operation_mode === "CHARGE" ? "Criar cobrança" : transaction.operation_mode === "INVOICE_ONLY" ? "Registrar e emitir NF" : "Registrar recebimento"}</Button>
                </footer>
            </form>
        </Modal>
    )
}

export default ModalNewTransaction
